/**
 * Server undo client (plan §8.11, record-ops' undo tokens). Every reversible record write returns
 * `undo: { token, expiresAt, label }`. `offer(token)` shows "Moved to Proposal · Undo" for 10 s and
 * puts the token on a short stack; ⌘Z (through `serverUndoCommands(scope)` in list and record scopes)
 * reverts the last one, ⌘⇧Z redoes it with the redo token the server returns.
 */
import { wallClock } from '@dataroom/core';
import { toast as uiToast } from '@dataroom/ui';
import { createContext, useContext, useSyncExternalStore } from 'react';
import { defineCommand } from './commands/define';
import type { CommandDef, Scope } from './commands/types';
import { ixT } from './i18n';

export type UndoTokenLike = { token: string; label: string; expiresAt: string };
export type UndoResultLike = {
  status: 'done' | 'partial' | 'failed' | 'queued';
  redo?: UndoTokenLike;
  results?: ReadonlyArray<{ ok: boolean; skippedFields?: readonly string[] }>;
};

export type ServerUndoOptions = {
  /** `POST /api/v1/undo/:token` (`api.records.undo`). */
  revert: (token: string) => Promise<UndoResultLike>;
  /** Called after an undo or redo so screens refetch (the app invalidates its record queries). */
  onReverted?: (result: UndoResultLike) => void;
  /** Milliseconds now (tests); token expiry is infrastructure time, so the wall clock by default. */
  now?: () => number;
  toast?: typeof uiToast;
};

type Entry = {
  token: UndoTokenLike;
  onReverted?: (result: UndoResultLike) => void;
  /** Id of the toast that offers this entry's Undo. */
  toastId?: string;
};

const MAX = 20;

/** Error with an HTTP status (the API client's `ApiError`). */
const statusOf = (err: unknown): number | undefined =>
  typeof err === 'object' && err !== null && 'status' in err
    ? Number((err as { status: unknown }).status)
    : undefined;

export class ServerUndoStore {
  private undoStack: Entry[] = [];
  private redoStack: Entry[] = [];
  private listeners = new Set<() => void>();
  private busy = false;
  private version = 0;
  private groupToasts = new Map<string, string>();

  constructor(private options: ServerUndoOptions) {}

  configure(options: Partial<ServerUndoOptions>): void {
    this.options = { ...this.options, ...options };
  }

  private now(): number {
    return this.options.now?.() ?? wallClock.nowMs();
  }

  private valid(entry: Entry): boolean {
    return Date.parse(entry.token.expiresAt) > this.now();
  }

  /** Offer Undo for a write: a toast now, and ⌘Z while a list or record scope is active. */
  offer(
    token: UndoTokenLike | undefined | null,
    options: {
      onReverted?: (r: UndoResultLike) => void;
      silent?: boolean;
      /**
       * Writes of one surface (successive cell edits of a grid) share a group: a newer offer replaces
       * the group's older Undo toast, so edits do not pile up toasts (⌘Z still walks back through all).
       */
      group?: string;
      /** Extra line under the title and one action beside Undo (e.g. what was left unchanged and why). */
      body?: string;
      action?: { label: string; onClick: () => void | Promise<void> };
      durationMs?: number;
      /** The toast's tone (default `ok`; `warn` when part of the change was left out). */
      tone?: 'ok' | 'warn';
    } = {},
  ): void {
    if (!token) return;
    const entry: Entry = { token, onReverted: options.onReverted };
    const toast = this.options.toast ?? uiToast;
    if (options.group) {
      const previous = this.groupToasts.get(options.group);
      if (previous) toast.dismiss?.(previous);
    }
    this.undoStack.push(entry);
    if (this.undoStack.length > MAX) this.undoStack.shift();
    this.redoStack = [];
    this.changed();
    if (!options.silent) {
      const id = toast({
        title: token.label,
        ...(options.body ? { body: options.body } : {}),
        ...(options.action ? { action: options.action } : {}),
        ...(options.durationMs !== undefined ? { durationMs: options.durationMs } : {}),
        tone: options.tone ?? 'ok',
        dedupeKey: `undo:${token.token}`,
        // `false` when the token could not be reverted (expired, taken by ⌘Z): no "Undone" then.
        undo: () => this.revertEntry(entry, 'undo'),
      });
      entry.toastId = id;
      if (options.group && id) this.groupToasts.set(options.group, id);
    }
  }

  canUndo(): boolean {
    return this.undoStack.some((e) => this.valid(e));
  }

  canRedo(): boolean {
    return this.redoStack.some((e) => this.valid(e));
  }

  /** Undo the newest change that can still be undone. */
  async undo(): Promise<boolean> {
    while (this.undoStack.length) {
      const entry = this.undoStack[this.undoStack.length - 1]!;
      if (!this.valid(entry)) {
        this.undoStack.pop();
        continue;
      }
      return this.revertEntry(entry, 'undo', true);
    }
    return false;
  }

  async redo(): Promise<boolean> {
    while (this.redoStack.length) {
      const entry = this.redoStack[this.redoStack.length - 1]!;
      if (!this.valid(entry)) {
        this.redoStack.pop();
        continue;
      }
      return this.revertEntry(entry, 'redo', true);
    }
    return false;
  }

  private async revertEntry(entry: Entry, direction: 'undo' | 'redo', announce = false): Promise<boolean> {
    if (this.busy) return false;
    this.busy = true;
    const toast = this.options.toast ?? uiToast;
    const from = direction === 'undo' ? this.undoStack : this.redoStack;
    const to = direction === 'undo' ? this.redoStack : this.undoStack;
    try {
      const result = await this.options.revert(entry.token.token);
      const i = from.indexOf(entry);
      if (i >= 0) from.splice(i, 1);
      // The Undo toast of a token that was just consumed (here or by ⌘Z) must not offer it again.
      if (entry.toastId) toast.dismiss?.(entry.toastId);
      if (result.redo) to.push({ token: result.redo, onReverted: entry.onReverted });
      entry.onReverted?.(result);
      this.options.onReverted?.(result);
      const skipped = (result.results ?? []).reduce((n, r) => n + (r.skippedFields?.length ?? 0), 0);
      if (result.status === 'partial' && skipped > 0)
        toast({ title: ixT('ix.undo.partial_one', { count: skipped }), tone: 'warn' });
      else if (announce)
        toast({
          title: `${ixT(direction === 'undo' ? 'ix.undo.done' : 'ix.redo.done')} · ${entry.token.label}`,
          tone: 'ok',
          ...(result.redo && direction === 'undo'
            ? { action: { label: ixT('ix.cmd.redo'), onClick: () => void this.redo() } }
            : {}),
        });
      return true;
    } catch (err) {
      const gone = statusOf(err) === 410;
      if (gone) {
        const i = from.indexOf(entry);
        if (i >= 0) from.splice(i, 1);
        if (entry.toastId) toast.dismiss?.(entry.toastId);
      }
      toast({
        title: gone ? ixT('ix.undo.expired') : ixT('ix.undo.failed'),
        body: !gone && err instanceof Error ? err.message : undefined,
        tone: 'err',
      });
      return false;
    } finally {
      this.busy = false;
      this.changed();
    }
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getVersion(): number {
    return this.version;
  }

  private changed(): void {
    this.version += 1;
    for (const l of [...this.listeners]) l();
  }
}

/** The app's store (configured by `InteractionRoot`). */
export const serverUndo = new ServerUndoStore({
  revert: () => Promise.reject(new Error('Server undo is not configured')),
});

const Ctx = createContext<ServerUndoStore>(serverUndo);
export const ServerUndoProvider = Ctx.Provider;

/** `offer(token)` after a reversible write; `undo` / `redo` for buttons. */
export function useServerUndo() {
  const store = useContext(Ctx);
  useSyncExternalStore(
    (l) => store.subscribe(l),
    () => store.getVersion(),
    () => 0,
  );
  return {
    offer: (
      token: UndoTokenLike | undefined | null,
      options?: { onReverted?: (r: UndoResultLike) => void; silent?: boolean; group?: string },
    ) => store.offer(token, options),
    undo: () => store.undo(),
    redo: () => store.redo(),
    canUndo: store.canUndo(),
    canRedo: store.canRedo(),
  };
}

/**
 * ⌘Z / ⌘⇧Z (Ctrl+Y on Windows) for a list or record scope: they revert the last server write. Text
 * fields keep their native undo.
 */
export function serverUndoCommands<Ctx>(
  scope: Scope<Ctx>,
  store: ServerUndoStore = serverUndo,
): CommandDef<Ctx>[] {
  return [
    defineCommand<Ctx>({
      id: `${scope.id}.serverUndo`,
      title: () => ixT('ix.cmd.undo'),
      shortcut: 'mod+z',
      scope,
      textSafe: false,
      when: () => store.canUndo(),
      run: () => void store.undo(),
    }),
    defineCommand<Ctx>({
      id: `${scope.id}.serverRedo`,
      title: () => ixT('ix.cmd.redo'),
      shortcut: ['mod+shift+z', { windows: 'ctrl+y' }],
      scope,
      textSafe: false,
      when: () => store.canRedo(),
      run: () => void store.redo(),
    }),
  ];
}
